/**
* Copyright (c) 2025 Bytedance, Inc. and its affiliates.
* SPDX-License-Identifier: Apache-2.0
*/
import * as __WEBPACK_EXTERNAL_MODULE_node_events_0a6aefe7__ from "node:events";
import * as __WEBPACK_EXTERNAL_MODULE_uuid__ from "uuid";
import * as __WEBPACK_EXTERNAL_MODULE_minimatch__ from "minimatch";
import * as __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_index_js_b6f47fb2__ from "@modelcontextprotocol/sdk/client/index.js";
import * as __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_sse_js_dfc8fb2d__ from "@modelcontextprotocol/sdk/client/sse.js";
import * as __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_stdio_js_6453b0e3__ from "@modelcontextprotocol/sdk/client/stdio.js";
import * as __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_streamableHttp_js_693c4734__ from "@modelcontextprotocol/sdk/client/streamableHttp.js";
import * as __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_inMemory_js_eefc70d7__ from "@modelcontextprotocol/sdk/inMemory.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
class MCPClient extends __WEBPACK_EXTERNAL_MODULE_node_events_0a6aefe7__.EventEmitter {
    log(level, ...args) {
        if (!this.isDebug && 'error' !== level) return;
        console[level](...args);
    }
    filterItems(items, filterConfig) {
        if (!filterConfig) return items;
        let filteredItems = items;
        if (filterConfig.allow && filterConfig.allow.length > 0) filteredItems = filteredItems.filter((item)=>filterConfig.allow.some((pattern)=>(0, __WEBPACK_EXTERNAL_MODULE_minimatch__.minimatch)(item.name, pattern)));
        if (filterConfig.block && filterConfig.block.length > 0) filteredItems = filteredItems.filter((item)=>!filterConfig.block.some((pattern)=>(0, __WEBPACK_EXTERNAL_MODULE_minimatch__.minimatch)(item.name, pattern)));
        return filteredItems;
    }
    getServersFromStore() {
        return this.store.get('mcp.servers') || [];
    }
    async init() {
        if (this.initialized) return;
        if (this.initPromise) return this.initPromise;
        this.initPromise = (async ()=>{
            try {
                this.log('info', '[MCP] Starting initialization');
                this.initialized = true;
                await this.load(this.getServersFromStore());
                this.log('info', '[MCP] Initialization completed successfully');
            } catch (err) {
                this.initialized = false;
                this.log('error', '[MCP] Failed to initialize:', err);
                throw err;
            } finally{
                this.initPromise = null;
            }
        })();
        return this.initPromise;
    }
    async ensureInitialized() {
        if (!this.initialized) {
            this.log('debug', '[MCP] Ensuring initialization');
            await this.init();
        }
    }
    getEnhancedPath(originalPath) {
        const pathSeparator = 'win32' === process.platform ? ';' : ':';
        const existingPaths = new Set(originalPath.split(pathSeparator).filter(Boolean));
        const homeDir = process.env.HOME || process.env.USERPROFILE || '';
        const newPaths = [];
        if ('darwin' === process.platform) newPaths.push('/bin', '/usr/bin', '/usr/local/bin', '/usr/local/sbin', '/opt/homebrew/bin', '/opt/homebrew/sbin', '/usr/local/opt/node/bin', `${homeDir}/.nvm/current/bin`, `${homeDir}/.npm-global/bin`, `${homeDir}/.yarn/bin`, `${homeDir}/.cargo/bin`, '/opt/local/bin');
        if ('linux' === process.platform) newPaths.push('/bin', '/usr/bin', '/usr/local/bin', `${homeDir}/.nvm/current/bin`, `${homeDir}/.npm-global/bin`, `${homeDir}/.yarn/bin`, `${homeDir}/.cargo/bin`, '/snap/bin');
        if ('win32' === process.platform) newPaths.push(`${process.env.APPDATA}\\npm`, `${homeDir}\\AppData\\Local\\Yarn\\bin`, `${homeDir}\\.cargo\\bin`);
        newPaths.forEach((path)=>{
            if (path && !existingPaths.has(path)) existingPaths.add(path);
        });
        return Array.from(existingPaths).join(pathSeparator);
    }
    async activate(server) {
        await this.ensureInitialized();
        try {
            const { name } = server;
            const client = new __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_index_js_b6f47fb2__.Client({
                name: name,
                version: '1.0.0'
            }, {
                capabilities: {}
            });
            let transport;
            if ('url' in server) {
                const { url, headers = {}, type = 'streamable-http' } = server;
                if ('streamable-http' === type) transport = new __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_streamableHttp_js_693c4734__.StreamableHTTPClientTransport(new URL(url), {
                    requestInit: {
                        headers
                    }
                });
                else if ('sse' === type) transport = new __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_sse_js_dfc8fb2d__.SSEClientTransport(new URL(url), {
                    eventSourceInit: {
                        fetch: (url, init)=>fetch(url, {
                                ...init,
                                headers
                            })
                    },
                    requestInit: {
                        headers
                    }
                });
                else throw new Error('Invalid server type');
                await client.connect(transport);
            } else if ('command' in server) {
                const { command, args, env, cwd } = server;
                let cmd = command;
                if ('win32' === process.platform) {
                    if ('npx' === command) cmd = `${command}.cmd`;
                    else if ('node' === command) cmd = `${command}.exe`;
                }
                const mergedEnv = {
                    PATH: this.getEnhancedPath(process.env.PATH || ''),
                    ...env
                };
                const transportOpts = {
                    command: cmd,
                    args,
                    stderr: 'win32' === process.platform ? 'pipe' : 'inherit',
                    env: mergedEnv,
                    ...cwd ? {
                        cwd
                    } : {}
                };
                transport = new __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_client_stdio_js_6453b0e3__.StdioClientTransport(transportOpts);
                await client.connect(transport);
            } else if ('mcpServer' in server) {
                const { mcpServer } = server;
                const [clientTransport, serverTransport] = __WEBPACK_EXTERNAL_MODULE__modelcontextprotocol_sdk_inMemory_js_eefc70d7__.InMemoryTransport.createLinkedPair();
                transport = clientTransport;
                await Promise.all([
                    client.connect(clientTransport),
                    mcpServer.connect(serverTransport)
                ]);
                this.log('info', `[MCP] Server ${name} started successfully`);
                this.emit('server-started', {
                    name
                });
            } else throw new Error('No command or url provided for server');
            this.clients[name] = client;
            this.activeServers.set(name, {
                client,
                server
            });
            this.log('info', `[MCP] Server ${name} started successfully`);
            this.emit('server-started', {
                name
            });
        } catch (error) {
            this.log('error', `[MCP] Error ${null == server ? void 0 : server.name} activating server:`, error);
            throw error;
        }
    }
    async load(servers) {
        this.log('info', `[MCP] Loading ${servers.length} servers`);
        const activeServers = servers.filter((server)=>'activate' === server.status);
        if (0 === activeServers.length) {
            this.log('info', '[MCP] No active servers to load');
            return;
        }
        for (const server of activeServers){
            this.log('info', `[MCP] Activating server: ${server.name}`);
            try {
                await this.activate(server);
                this.log('info', `[MCP] Successfully activated server: ${server.name}`);
            } catch (error) {
                this.log('error', `[MCP] Failed to activate server ${server.name}:`, error);
                this.emit('server-error', {
                    name: server.name,
                    error
                });
            }
        }
        this.log('info', `[MCP] Loaded and activated ${Object.keys(this.clients).length} servers`);
    }
    async listAvailableServices() {
        await this.ensureInitialized();
        return this.getServersFromStore();
    }
    async addServer(server) {
        await this.ensureInitialized();
        try {
            const servers = this.getServersFromStore();
            if (servers.some((s)=>s.name === server.name)) throw new Error(`Server with name ${server.name} already exists`);
            servers.push(server);
            this.store.set('mcp.servers', servers);
            if ('activate' === server.status) try {
                await this.activate(server);
            } catch (activationError) {
                this.log('error', `Failed to activate server ${server.name}:`, activationError);
                this.emit('server-error', {
                    name: server.name,
                    error: activationError
                });
                throw activationError;
            }
        } catch (error) {
            this.log('error', `Failed to add MCP server: ${error}`);
            throw error;
        }
    }
    async getServer(name) {
        await this.ensureInitialized();
        try {
            const servers = this.getServersFromStore();
            const server = servers.find((s)=>s.name === name);
            if (!server) throw new Error(`Server ${name} not found`);
            return server;
        } catch (error) {
            this.log('error', '[MCP] Error deactivating server:', error);
            return;
        }
    }
    async deactivate(name) {
        await this.ensureInitialized();
        try {
            if (this.clients[name]) {
                this.log('info', `[MCP] Stopping server: ${name}`);
                await this.clients[name].close();
                delete this.clients[name];
                this.activeServers.delete(name);
                this.emit('server-stopped', {
                    name
                });
            } else this.log('warn', `[MCP] Server ${name} is not running`);
        } catch (error) {
            this.log('error', '[MCP] Error deactivating server:', error);
            throw error;
        }
    }
    async updateServer(server) {
        await this.ensureInitialized();
        try {
            const servers = this.getServersFromStore();
            const index = servers.findIndex((s)=>s.name === server.name);
            if (-1 === index) throw new Error(`Server ${server.name} not found`);
            const wasActive = 'activate' === servers[index].status;
            if (wasActive && 'activate' !== server.status) await this.deactivate(server.name);
            else if (!wasActive && 'activate' === server.status) await this.activate(server);
            servers[index] = server;
            this.store.set('mcp.servers', servers);
        } catch (error) {
            this.log('error', 'Failed to update MCP server:', error);
            throw error;
        }
    }
    async deleteServer(serverName) {
        await this.ensureInitialized();
        try {
            if (this.clients[serverName]) await this.deactivate(serverName);
            const servers = this.getServersFromStore();
            const filteredServers = servers.filter((s)=>s.name !== serverName);
            this.store.set('mcp.servers', filteredServers);
        } catch (error) {
            this.log('error', 'Failed to delete MCP server:', error);
            throw error;
        }
    }
    async setServerActive(params) {
        await this.ensureInitialized();
        try {
            const { name, isActive } = params;
            const servers = this.getServersFromStore();
            const server = servers.find((s)=>s.name === name);
            if (!server) throw new Error(`Server ${name} not found`);
            server.status = isActive ? 'activate' : 'error';
            this.store.set('mcp.servers', servers);
            if (isActive) await this.activate(server);
            else await this.deactivate(name);
        } catch (error) {
            this.log('error', 'Failed to set MCP server active status:', error);
            throw error;
        }
    }
    async checkServerStatus(server) {
        await this.activate(server);
        await this.deactivate(server.name);
    }
    async listTools(serverName) {
        await this.ensureInitialized();
        try {
            if (serverName) {
                var _this_activeServers_get, _server_filters;
                if (!this.clients[serverName]) throw new Error(`MCP Client ${serverName} not found`);
                const { tools } = await this.clients[serverName].listTools();
                const server = null === (_this_activeServers_get = this.activeServers.get(serverName)) || void 0 === _this_activeServers_get ? void 0 : _this_activeServers_get.server;
                let processedTools = tools.map((tool)=>{
                    tool.serverName = serverName;
                    tool.id = 'f' + (0, __WEBPACK_EXTERNAL_MODULE_uuid__.v4)().replace(/-/g, '');
                    tool.description = tool.description || `${serverName} - ${tool.name}`;
                    return tool;
                });
                if (null == server ? void 0 : null === (_server_filters = server.filters) || void 0 === _server_filters ? void 0 : _server_filters.tools) processedTools = this.filterItems(processedTools, server.filters.tools);
                return processedTools;
            }
            {
                let allTools = [];
                for(const clientName in this.clients)try {
                    var _this_activeServers_get1, _server_filters1;
                    this.log('info', `[MCP] Listing tools for ${clientName}`);
                    const { tools } = await this.clients[clientName].listTools();
                    const server = null === (_this_activeServers_get1 = this.activeServers.get(clientName)) || void 0 === _this_activeServers_get1 ? void 0 : _this_activeServers_get1.server;
                    this.log('info', `[MCP] Tools for ${clientName}:`, tools);
                    let processedTools = tools.map((tool)=>{
                        tool.serverName = clientName;
                        tool.id = 'f' + (0, __WEBPACK_EXTERNAL_MODULE_uuid__.v4)().replace(/-/g, '');
                        tool.description = tool.description || `${clientName} - ${tool.name}`;
                        return tool;
                    });
                    if (null == server ? void 0 : null === (_server_filters1 = server.filters) || void 0 === _server_filters1 ? void 0 : _server_filters1.tools) processedTools = this.filterItems(processedTools, server.filters.tools);
                    allTools = allTools.concat(processedTools);
                } catch (error) {
                    this.log('error', `[MCP] Error listing tools for ${clientName}:`, error);
                }
                this.log('info', `[MCP] Total tools listed: ${allTools.length}`);
                return allTools;
            }
        } catch (error) {
            this.log('error', '[MCP] Error listing tools:', error);
            return [];
        }
    }
    async listPrompts(serverName) {
        await this.ensureInitialized();
        try {
            if (serverName) {
                var _this_activeServers_get, _server_filters;
                if (!this.clients[serverName]) throw new Error(`MCP Client ${serverName} not found`);
                const { prompts } = await this.clients[serverName].listPrompts();
                const server = null === (_this_activeServers_get = this.activeServers.get(serverName)) || void 0 === _this_activeServers_get ? void 0 : _this_activeServers_get.server;
                let processedPrompts = prompts.map((prompt)=>({
                        ...prompt,
                        serverName,
                        id: 'p' + (0, __WEBPACK_EXTERNAL_MODULE_uuid__.v4)().replace(/-/g, '')
                    }));
                if (null == server ? void 0 : null === (_server_filters = server.filters) || void 0 === _server_filters ? void 0 : _server_filters.prompts) processedPrompts = this.filterItems(processedPrompts, server.filters.prompts);
                return processedPrompts;
            }
            {
                let allPrompts = [];
                for(const clientName in this.clients)try {
                    var _this_activeServers_get1, _server_filters1;
                    this.log('info', `[MCP] Listing prompts for ${clientName}`);
                    const { prompts } = await this.clients[clientName].listPrompts();
                    const server = null === (_this_activeServers_get1 = this.activeServers.get(clientName)) || void 0 === _this_activeServers_get1 ? void 0 : _this_activeServers_get1.server;
                    this.log('info', `[MCP] Prompts for ${clientName}:`, prompts);
                    let processedPrompts = prompts.map((prompt)=>({
                            ...prompt,
                            serverName: clientName,
                            id: 'p' + (0, __WEBPACK_EXTERNAL_MODULE_uuid__.v4)().replace(/-/g, '')
                        }));
                    if (null == server ? void 0 : null === (_server_filters1 = server.filters) || void 0 === _server_filters1 ? void 0 : _server_filters1.prompts) processedPrompts = this.filterItems(processedPrompts, server.filters.prompts);
                    allPrompts = allPrompts.concat(processedPrompts);
                } catch (error) {
                    this.log('error', `[MCP] Error listing prompts for ${clientName}:`, error);
                }
                this.log('info', `[MCP] Total prompts listed: ${allPrompts.length}`);
                return allPrompts;
            }
        } catch (error) {
            this.log('error', '[MCP] Error listing prompts:', error);
            return [];
        }
    }
    async callTool(params) {
        await this.ensureInitialized();
        try {
            const { client, name, args } = params;
            if (!this.clients[client]) throw new Error(`MCP Client ${client} not found`);
            const server = await this.getServer(client);
            this.log('info', '[MCP] Calling:', client, name, args);
            const timeoutSeconds = (null == server ? void 0 : server.timeout) ?? this.defaultTimeout;
            const result = await this.clients[client].callTool({
                name,
                arguments: args
            }, void 0, {
                timeout: 1000 * timeoutSeconds
            });
            this.log('info', '[MCP] Call Tool Result:', result);
            return result;
        } catch (error) {
            this.log('error', `[MCP] Error calling tool ${params.name} on ${params.client}:`, error);
            throw error;
        }
    }
    async cleanup() {
        try {
            for(const name in this.clients)await this.deactivate(name).catch((err)=>{
                this.log('error', `[MCP] Error during cleanup of ${name}:`, err);
            });
            this.clients = {};
            this.activeServers.clear();
            this.log('info', '[MCP] All servers cleaned up');
        } catch (error) {
            this.log('error', '[MCP] Failed to clean up servers:', error);
            throw error;
        }
    }
    constructor(servers, options){
        super(), _define_property(this, "activeServers", new Map()), _define_property(this, "clients", {}), _define_property(this, "initialized", false), _define_property(this, "initPromise", null), _define_property(this, "store", new Map()), _define_property(this, "isDebug", void 0), _define_property(this, "defaultTimeout", void 0);
        this.isDebug = (null == options ? void 0 : options.isDebug) || 'mcp' === process.env.DEBUG || false;
        this.defaultTimeout = (null == options ? void 0 : options.defaultTimeout) || 60;
        this.store.set('mcp.servers', servers.map((s)=>({
                ...s,
                status: s.status || 'activate'
            })));
    }
}
export { MCPClient };
